import { ref, watch, type Component } from 'vue'
import { defineStore } from 'pinia'
import AuditSlide from '@/components/audit/AuditSlide.vue'
import MethodHistorySlide from '@/components/methods/MethodHistorySlide.vue'
import i18n from '@/i18n'
import UserSlide from '@/components/user/UserSlide.vue'
import DataAssetSlide from '@/components/data_asset/DataAssetSlide.vue'
import AssetSlide from '@/components/asset/AssetSlide.vue'
import SubAssetSlide from '@/components/sub_asset/SubAssetSlide.vue'
import InstanceSlide from '@/components/instance/InstanceSlide.vue'
import BpSlide from '@/components/business_processes/BpSlide.vue'
import AddModule from '@/components/module/slide/AddModule.vue'
import EditModule from '@/components/module/slide/EditModule.vue'
import ViewModule from '@/components/module/slide/ViewModule.vue'
import { cloneDeep, isEqual } from 'lodash'
import type { Asset } from '@/models/api-generated'
import JournalSlide from '@/components/journal/JournalSlide.vue'
import type { JournalFilter } from '@/models/filters-generated'
import type { Tabs } from '@/types/module'

export default defineStore('slider', () => {
	const isVisible = ref(false)
	const title = ref<string>()
	const component = ref<Component>()
	const footer = ref<Component>()
	const unsavedChanges = ref(false)
	const cancelCallback = ref<(() => void) | null>(null)
	const showUnsavedChangesModal = ref(false)

	const confirmDeleteModal = ref<{
		show: boolean
		message: string
		type: string
		onConfirm?: () => void
	}>({
		show: false,
		message: '',
		type: '',
	})

	function requestDeleteConfirm(message: string, type: string, onConfirm: () => void) {
		confirmDeleteModal.value = { show: true, message, type, onConfirm }
	}

	function confirmDelete() {
		confirmDeleteModal.value.onConfirm?.()
		confirmDeleteModal.value.show = false
	}

	function cancelConfirmDelete() {
		confirmDeleteModal.value.show = false
	}

	function requestCancel(callback: () => void) {
		cancelCallback.value = callback
		showUnsavedChangesModal.value = true
	}

	function confirmCancel() {
		if (cancelCallback.value) {
			cancelCallback.value()
			cancelCallback.value = null
		}
		isVisible.value = false
		setUnsavedChanges(false)
		showUnsavedChangesModal.value = false
	}

	function cancelCancel() {
		cancelCallback.value = null
		showUnsavedChangesModal.value = false
	}

	function setUnsavedChanges(val: boolean) {
		unsavedChanges.value = val
	}

	function getHasUnsavedChanges() {
		return unsavedChanges.value
	}

	const getComponent = () => component.value
	const getFooter = () => footer.value
	const setFooter = (c?: Component) => (footer.value = c)
	const close = () => (isVisible.value = false)

	function openCommon(c: Component, t: string) {
		if (unsavedChanges.value) return
		setTimeout(() => {
			title.value = t
			component.value = c
			footer.value = undefined
			isVisible.value = true
		}, 0)
	}

	const openAudit = () => openCommon(<AuditSlide />, i18n.global.t('audit.label'))

	const openMethodHistory = (metadataId: number, methodReference: string, assetName: string) =>
		openCommon(
			<MethodHistorySlide metadataId={metadataId} reference={methodReference} assetName={assetName} />,
			i18n.global.t('history.label'),
		)

	const openUser = (userId = 0, name?: string) =>
		openCommon(<UserSlide userId={userId} />, userId === 0 ? i18n.global.t('misc.add') : name || '')

	const openDataAsset = (assetId = 0, name?: string) =>
		openCommon(<DataAssetSlide assetId={assetId} />, assetId === 0 ? i18n.global.t('misc.add') : name || '')

	const openAsset = (assetId = 0, name?: string) =>
		openCommon(<AssetSlide assetId={assetId} />, assetId === 0 ? i18n.global.t('misc.add') : name || '')

	const openSubAsset = (asset: Asset, subAssetId = 0, name?: string) =>
		openCommon(<SubAssetSlide asset={asset} subAssetId={subAssetId} />, name || '')

	const openInstance = (instanceId = 0, name?: string) =>
		openCommon(<InstanceSlide instanceId={instanceId} />, instanceId === 0 ? i18n.global.t('misc.add') : name || '')

	const openBp = (bpId = 0, name?: string) => openCommon(<BpSlide bpId={bpId} />, bpId === 0 ? i18n.global.t('misc.add') : name || '')

	function openAddModule() {
		setTimeout(() => {
			title.value = i18n.global.t('module.addModule')
			component.value = <AddModule />
			isVisible.value = true
		}, 0)
	}

	function openViewModule(metaId: number, name: string, isCustom: boolean, initialTab?: Tabs) {
		setTimeout(() => {
			title.value = name || i18n.global.t('module.label')
			component.value = <ViewModule metaId={metaId} name={name} isCustom={isCustom} initialTab={initialTab} />
			isVisible.value = true
		}, 0)
	}

	function openEditModule(metaId: number, name: string, isCustom: boolean, initialTab?: Tabs) {
		setTimeout(() => {
			title.value = name || i18n.global.t('module.editModule')
			component.value = <EditModule metaId={metaId} name={name} isCustom={isCustom} initialTab={initialTab} />
			isVisible.value = true
		}, 0)
	}

	function openJournal(label: string, filters: JournalFilter[]) {
		setTimeout(() => {
			title.value = label
			component.value = <JournalSlide filters={filters} />
			isVisible.value = true
		}, 0)
	}

	function hasUnsavedChanges(originalModel: any, model: any) {
		if (!originalModel) return false
		return !isEqual(model, originalModel)
	}

	function watchChanges(model: any) {
		const originalModel = cloneDeep(model.value)
		watch(
			model,
			() => {
				if (!isVisible.value) return
				setUnsavedChanges(hasUnsavedChanges(originalModel, model))
			},
			{ deep: true },
		)
	}

	return {
		isVisible,
		title,
		showUnsavedChangesModal,
		confirmDeleteModal,
		getComponent,
		getFooter,
		setFooter,
		close,
		openAudit,
		openMethodHistory,
		openUser,
		openDataAsset,
		openAsset,
		openSubAsset,
		openInstance,
		openBp,
		openAddModule,
		openViewModule,
		openEditModule,
		openJournal,
		setUnsavedChanges,
		getHasUnsavedChanges,
		hasUnsavedChanges,
		watchChanges,
		requestCancel,
		confirmCancel,
		cancelCancel,
		requestDeleteConfirm,
		confirmDelete,
		cancelConfirmDelete,
	}
})
